import { ApiError, type PlayerApiClient, type DeviceDescriptor } from './api-client.js';
import type { DatabaseAdapter, LoggerAdapter } from './adapters.js';
import type { PlayerRuntime } from './runtime.js';

export interface PairingState {
  status: 'idle' | 'requesting' | 'waiting' | 'paired' | 'error';
  code: string | null;
  expiresAt: string | null;
  apiBaseUrl: string;
  message: string | null;
  screenName?: string | null;
  organizationName?: string | null;
}

export interface PairingOptions {
  onUpdate: (state: PairingState) => void;
  device: DeviceDescriptor;
  defaultBaseUrl: string;
}

/**
 * Drives the pairing flow shown on the TV.
 *
 * Requests a code, displays it, polls for the claim, then stores the device token. A code that
 * expires is replaced automatically so an unattended screen always shows a usable code.
 */
export class PairingController {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private current: PairingState;

  constructor(
    private readonly api: PlayerApiClient,
    private readonly runtime: PlayerRuntime,
    private readonly db: DatabaseAdapter,
    private readonly logger: LoggerAdapter,
    private readonly options: PairingOptions,
  ) {
    this.current = {
      status: 'idle',
      code: null,
      expiresAt: null,
      apiBaseUrl: options.defaultBaseUrl,
      message: null,
    };
  }

  get state(): PairingState {
    return { ...this.current };
  }

  private update(patch: Partial<PairingState>): void {
    this.current = { ...this.current, ...patch };
    this.options.onUpdate(this.state);
  }

  async start(): Promise<void> {
    this.stop();
    this.stopped = false;
    await this.requestCode();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(fn: () => void, delayMs: number): void {
    if (this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(fn, delayMs);
    (this.timer as { unref?: () => void }).unref?.();
  }

  private async requestCode(): Promise<void> {
    this.update({ status: 'requesting', message: null });
    try {
      const result = await this.api.requestPairing(this.options.device);
      this.logger.info('Pairing code issued', { code: result.code });
      this.update({
        status: 'waiting',
        code: result.code,
        expiresAt: result.expiresAt,
        message: null,
      });
      this.poll(result.pollToken, result.expiresAt, Math.max(2, result.pollIntervalSec));
    } catch (err) {
      const message = (err as Error).message ?? 'Could not reach the server';
      this.logger.warn('Pairing request failed', { error: message });
      this.update({
        status: 'error',
        message: `Cannot reach ${this.current.apiBaseUrl}. Retrying…`,
      });
      // Keep retrying: the network may come back before anyone attends the screen.
      this.schedule(() => void this.requestCode(), 15_000);
    }
  }

  private poll(pollToken: string, expiresAt: string, intervalSec: number): void {
    this.schedule(async () => {
      if (this.stopped) return;
      try {
        const status = await this.api.pairingStatus(pollToken);
        if (status.status === 'paired') {
          this.logger.info('Device paired', {
            screenId: status.screenId,
            screen: status.screenName,
          });
          await this.runtime.completePairing({
            deviceToken: status.deviceToken,
            screenId: status.screenId,
            organizationId: status.organizationId,
            apiBaseUrl: status.apiBaseUrl,
          });
          this.update({
            status: 'paired',
            code: null,
            message: null,
            screenName: status.screenName,
            organizationName: status.organizationName,
          });
          this.stop();
          return;
        }
        if (status.status === 'expired' || new Date(expiresAt).getTime() < Date.now()) {
          this.logger.info('Pairing code expired, requesting a new one');
          await this.requestCode();
          return;
        }
        this.poll(pollToken, expiresAt, intervalSec);
      } catch (err) {
        const error = err as ApiError;
        // A transient failure should not clear the code from the screen.
        this.logger.debug('Pairing poll failed', { error: error.message });
        if (new Date(expiresAt).getTime() < Date.now()) {
          await this.requestCode();
        } else {
          this.poll(pollToken, expiresAt, intervalSec);
        }
      }
    }, intervalSec * 1000);
  }

  /** Lets an installer point the player at a different server before pairing. */
  async setApiBaseUrl(url: string): Promise<void> {
    const normalized = url.replace(/\/$/, '');
    this.db.setSetting('apiBaseUrl', normalized);
    this.api.setBaseUrl(normalized);
    this.update({ apiBaseUrl: normalized, message: null });
    await this.start();
  }
}
